import { create } from 'zustand';
import { ruleApi } from '../services/ruleApi';
import {
  RuleTemplate,
  RuleApplication,
  InputSchema,
  DetectAndApplyResponse,
} from '../types/rule';

interface RuleState {
  // 规则列表
  rules: RuleTemplate[];
  // 当前选中的规则
  selectedRule: RuleTemplate | null;
  // 应用记录
  applications: RuleApplication[];
  // 加载状态
  isLoading: boolean;
  isApplying: boolean;
  // 错误信息
  error: string | null;
  // 匹配结果
  matchResult: DetectAndApplyResponse | null;

  // 操作
  setRules: (rules: RuleTemplate[]) => void;
  setSelectedRule: (rule: RuleTemplate | null) => void;
  fetchRules: () => Promise<void>;
  createRule: (data: {
    name: string;
    description?: string;
    workflowId: string;
    inputSchema: InputSchema;
  }) => Promise<RuleTemplate | null>;
  updateRule: (id: string, data: Partial<RuleTemplate>) => Promise<void>;
  deleteRule: (id: string) => Promise<void>;
  applyRule: (ruleId: string, inputFileId: string) => Promise<RuleApplication | null>;
  detectAndApply: (inputFileId: string, autoApply?: boolean) => Promise<DetectAndApplyResponse | null>;
  fetchApplications: (ruleId: string) => Promise<void>;
  clearMatchResult: () => void;
  clearError: () => void;
}

export const useRuleStore = create<RuleState>()((set) => ({
  rules: [],
  selectedRule: null,
  applications: [],
  isLoading: false,
  isApplying: false,
  error: null,
  matchResult: null,

  setRules: (rules) => set({ rules }),
  setSelectedRule: (rule) => set({ selectedRule: rule }),

  fetchRules: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await ruleApi.listRules();
      if (response.code === 0) {
        set({ rules: response.data.rules });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isLoading: false });
    }
  },

  createRule: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await ruleApi.createRule(data);
      if (response.code === 0) {
        set((state) => ({
          rules: [response.data, ...state.rules],
        }));
        return response.data;
      }
      return null;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },

  updateRule: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await ruleApi.updateRule(id, data);
      if (response.code === 0) {
        set((state) => ({
          rules: state.rules.map((r) => (r.id === id ? response.data : r)),
          selectedRule: state.selectedRule?.id === id ? response.data : state.selectedRule,
        }));
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isLoading: false });
    }
  },

  deleteRule: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await ruleApi.deleteRule(id);
      set((state) => ({
        rules: state.rules.filter((r) => r.id !== id),
        selectedRule: state.selectedRule?.id === id ? null : state.selectedRule,
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isLoading: false });
    }
  },

  applyRule: async (ruleId, inputFileId) => {
    set({ isApplying: true, error: null });
    try {
      const response = await ruleApi.applyRule(ruleId, inputFileId);
      if (response.code === 0) {
        set((state) => ({
          applications: [response.data, ...state.applications],
        }));
        return response.data;
      }
      return null;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
      return null;
    } finally {
      set({ isApplying: false });
    }
  },

  detectAndApply: async (inputFileId, autoApply = false) => {
    set({ isApplying: true, error: null, matchResult: null });
    try {
      const response = await ruleApi.detectAndApply(inputFileId, autoApply);
      if (response.code === 0) {
        set({ matchResult: response.data });
        if (response.data.application) {
          set((state) => ({
            applications: [response.data.application!, ...state.applications],
          }));
        }
        return response.data;
      }
      return null;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
      return null;
    } finally {
      set({ isApplying: false });
    }
  },

  fetchApplications: async (ruleId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await ruleApi.listApplications(ruleId);
      if (response.code === 0) {
        set({ applications: response.data.applications });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      set({ isLoading: false });
    }
  },

  clearMatchResult: () => set({ matchResult: null }),
  clearError: () => set({ error: null }),
}));
